/**
 * @file app/error.tsx
 * @description Global error boundary for BikeParkMapa
 * @module app/error
 * @note This must be a Client Component as per Next.js requirements
 */

'use client';

import { useEffect } from 'react';
import Link from 'next/link';
import { AlertTriangle, RotateCw, Home, Map } from 'lucide-react';

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // Log error to console for debugging
    console.error('Global error boundary caught:', error);
  }, [error]);

  return (
    <div className="content-page">
      <div className="error-container" style={{
        minHeight: '60vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: '2rem',
      }}>
        <div style={{ marginBottom: '1rem' }}>
          <AlertTriangle size={96} color="#dc2626" />
        </div>
        <h1 style={{ fontSize: '2.5rem', marginBottom: '1rem', color: 'var(--error-color, #dc2626)' }}>
          Ups! Coś poszło nie tak
        </h1>
        <p style={{ fontSize: '1.2rem', marginBottom: '2rem', maxWidth: '600px', color: 'var(--text-secondary, #666)' }}>
          Wystąpił nieoczekiwany błąd podczas ładowania tej strony. Przepraszamy za utrudnienia.
        </p>

        {process.env.NODE_ENV === 'development' && (
          <div style={{
            marginBottom: '2rem',
            padding: '1rem',
            backgroundColor: '#fee',
            border: '1px solid #fcc',
            borderRadius: '4px',
            maxWidth: '800px',
            textAlign: 'left',
            fontSize: '0.9rem',
            fontFamily: 'monospace',
            overflow: 'auto',
          }}>
            <strong>Error details (development only):</strong>
            <pre style={{ marginTop: '0.5rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {error.message}
            </pre>
            {error.digest && (
              <div style={{ marginTop: '0.5rem' }}>
                <strong>Digest:</strong> {error.digest}
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', justifyContent: 'center' }}>
          <button
            onClick={() => reset()}
            className="btn btn-primary"
            style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
          >
            <RotateCw size={18} /> Spróbuj ponownie
          </button>
          <Link href="/pl" className="btn btn-secondary" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
            <Home size={18} /> Strona główna
          </Link>
          <Link href="/pl/bikeparki" className="btn btn-secondary" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
            <Map size={18} /> Mapa bikeparków
          </Link>
        </div>

        <div style={{ marginTop: '3rem', fontSize: '0.9rem', color: 'var(--text-secondary, #999)' }}>
          <p>Jeśli problem się powtarza, skontaktuj się z nami:</p>
          <p style={{ marginTop: '0.5rem' }}>
            <a href="https://github.com/anthropics/claude-code/issues" style={{ color: 'var(--primary-color, #16a34a)' }}>
              Zgłoś problem
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
